import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { DateTime } from 'luxon';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Button from '@material-ui/core/Button';
import InputIcon from '@material-ui/icons/Input';
import AddIcon from '@material-ui/icons/Add';
import CalcIcon from 'components/Icons/CalcIcon';
import EmployerIcon from 'components/Icons/EmployerIcon';
import ErrorIcon from '@material-ui/icons/Error';
import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { formatAmountValue, sqlDateToLabel, pluralize, fractionToPercent, compareChartDates } from 'helpers/helpers';
import DepositSingleIcon from 'components/Icons/DepositSingleIcon';
import DepositRegularIcon from 'components/Icons/DepositRegularIcon';
import DepositEmployerIcon from 'components/Icons/DepositEmployerIcon';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Tooltip from 'components/Tooltip/Tooltip';
import FreeSwitch from 'components/FormFields/FreeSwitch';
import Info from 'components/Tooltip/Info';

import Employers from 'pages/Accounts/Edit/components/Employers';
import { CalculationMode } from 'common/interfaces';
import { RootStore } from 'stores/rootStore';
import { DepositsStore } from 'stores/workflowStores/finances/depositsStore/depositsStore';
import {
  Deposit,
  DepositFrequency,
  DepositsData,
  DepositType,
} from 'stores/workflowStores/finances/depositsStore/model';
import useStyles from 'pages/Accounts/Edit/components/styles/moduleStyles';
import DepositModal from './DepositModal';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import { EdwardColors, Currency, AccountMode } from 'enums';
import { EmployersData } from 'stores/workflowStores/finances/employers/model';
import { API_ACCOUNT_DETAIL_EMPLOYERS } from 'api/endpoints';
import { TargetFrequency } from 'stores/workflowStores/finances/targets/model';

interface Props {
  accountId: string;
  rootStore: RootStore;
  data?: DepositsData;
  readOnly?: boolean;
  userMode?: boolean;
  viewMode?: ViewMode;
  mode: AccountMode;
  plan?: boolean;
  currency: Currency;
  refresh?: () => void;
  planStore?: WorkflowPlanStore;
  dashboard?: boolean;
  handleDepositModal?: Function;
  defaultInflation?: number;
  defaultInflationType: string;
  overview?: boolean;
  original?: boolean;
  locked?: boolean;
  targetsFullfilled?: boolean;
  updateTimestamp?: number;
}

const Deposits: React.FC<Props> = ({
  rootStore,
  currency,
  readOnly,
  userMode,
  viewMode,
  mode,
  data,
  accountId,
  refresh,
  plan,
  planStore,
  dashboard,
  handleDepositModal,
  defaultInflation,
  defaultInflationType,
  overview,
  original,
  locked,
  targetsFullfilled,
  updateTimestamp,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [depositData, setDepositData] = useState<Deposit | undefined>(undefined);
  const [refreshView, setRefreshView] = useState(false);
  const [refreshEmployers, setRefreshEmployers] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | undefined>(undefined);
  const [confirmDialogType, setConfirmDialogType] = useState<DepositType | undefined>(undefined);
  const [employersDialog, setEmployersDialog] = useState(false);
  const [employersOk, setEmployersOk] = useState(true);

  const [depositsStore] = useState(new DepositsStore(rootStore, accountId, viewMode));

  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'), { noSsr: true });

  useEffect(() => {
    if (!data || refreshView) {
      depositsStore.fetchDeposits();
      setRefreshView(false);
    }
  }, [data, depositsStore, refreshView]);

  useEffect(() => {
    depositsStore.resetCalculatedDeposit();
  }, [updateTimestamp, depositsStore]);

  const editDepositId = planStore?.editDeposit;

  useEffect(() => {
    if (editDepositId) {
      const deposit = data?.deposits.filter((d) => d.id === editDepositId) || null;
      deposit && editDeposit(deposit[0]);
    }
  }, [data, editDepositId]);

  useEffect(() => {
    (async () => {
      if (mode === AccountMode.LTIP && viewMode === ViewMode.DETAIL && data?.employerAllowed) {
        const employers = await rootStore.stores.apiStore.get<EmployersData>(API_ACCOUNT_DETAIL_EMPLOYERS(accountId));
        setEmployersOk(!!employers?.data?.employers.length);
      }
      setRefreshEmployers(false);
    })();
  }, [mode, viewMode, data?.employerAllowed, accountId, rootStore.stores.apiStore, refreshEmployers]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    //depositsStore.resetCalculatedDeposit();
    planStore && planStore.setEditDeposit(null);
  };

  const handleRefresh = async () => {
    if (refresh) {
      await refresh();
    } else {
      setRefreshView(true);
    }
  };

  data = data ? data : depositsStore.allDeposits || undefined;

  //data?.regularUntil &&
  const calculateConditionsMatch =
    !!data!.periodic &&
    compareChartDates(data!.periodic.until, DateTime.now().toSQLDate()) === 1 &&
    !!data?.deposits?.some((d) => d.inflation?.enabled) === false &&
    (!!(planStore?.data?.targetData && planStore.data.targetData.targets.length > 0) ||
      !!(
        planStore?.data?.rentData && planStore.data.rentData.portfolioRentCalculationMode !== CalculationMode.CALCULATED
      ));

  const calculateInaccTarget = !!planStore?.data?.targetData?.targets?.some(
    (target) => target.active && target.frequency !== TargetFrequency.SINGLE,
  );

  const editDeposit = (data: Deposit) => {
    setDepositData(data);
    setIsModalOpen(true);
  };

  const renderCalcButton = (data: Deposit) => {
    return (
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          depositsStore.calculateRegularDeposit();
          editDeposit(data);
        }}
        startIcon={<CalcIcon fontSize="medium" transparentBgr />}
        disabled={readOnly || !calculateConditionsMatch || !!depositsStore.calcResult || data.inflation?.enabled}
        style={{
          color: '#000',
          marginRight: '16px',
          borderRadius: '8px',
          pointerEvents: 'all',
          backgroundColor:
            readOnly || !calculateConditionsMatch ? undefined : targetsFullfilled ? EdwardColors.LIGHTER : undefined,
        }}
      >
        {t('deposits_calculate')}
        <Info
          content={t(
            `deposits_calculate${!calculateConditionsMatch || data.inflation?.enabled ? 'Disabled' : ''}_tooltip`,
          )}
          black
          inline
        />
      </Button>
    );
  };

  const renderDeposit = (data: Deposit) => {
    if (data.type === undefined) {
      data.type = DepositType.FUTURE;
    }
    const last = data.type === DepositType.LAST;
    return (
      <Grid container item xs={12} sm={12} md={12} lg={plan ? 6 : 12} spacing={1} key={data.id}>
        <Grid container item alignContent="center" alignItems="center" className={`${classes.item} ${overview && original ? classes.inactive : classes.deposit}`}>
          <Grid item xs={2} sm={2} xl={1} className={classes.icon}>
            {data.type === DepositType.SINGLE || data.frequency === DepositFrequency.SINGLE ? (
              <DepositSingleIcon
                fontSize="inherit"
                htmlColor={overview && original ? EdwardColors.GRAY_MEDIUM : undefined}
              />
            ) : data.type === DepositType.EMPLOYER ? (
              <DepositEmployerIcon
                fontSize="inherit"
                htmlColor={overview && original ? EdwardColors.GRAY_MEDIUM : undefined}
              />
            ) : (
              <DepositRegularIcon
                fontSize="inherit"
                htmlColor={overview && original ? EdwardColors.GRAY_MEDIUM : undefined}
              />
            )}
          </Grid>
          <Grid
            container
            item
            xs={(userMode || overview) && !last ? 10 : last ? 7 : data.type === DepositType.REGULAR && !mobile ? 5 : 8}
            sm={(userMode || overview) && !last ? 10 : last ? 7 : data.type === DepositType.REGULAR && !mobile ? 5 : 8}
            xl={(userMode || overview) && !last ? 11 : last ? 8 : data.type === DepositType.REGULAR && !mobile ? 6 : 9}
            spacing={1}
            className={classes.info}
          >
            <Grid item>
              <Typography variant="h3" className={`${classes.title}`}>
                {data.title || t(`deposits_${data.type}`)}
              </Typography>
              {data.type === DepositType.FUTURE || data.type === DepositType.EMPLOYER ? (
                <>
                  {data.frequency === DepositFrequency.SINGLE ? (
                    <span>{sqlDateToLabel(data.since!)}</span>
                  ) : (
                    <>
                      <span>
                        {t('general_since')}:&nbsp;{sqlDateToLabel(data.since!)}
                      </span>
                      <span>
                        {t('general_until')}:&nbsp;{sqlDateToLabel(data.until!)}
                      </span>
                    </>
                  )}
                  <span>
                    <strong>{formatAmountValue(data.value!.value, currency).replace(/ /g, '\u00A0')}&nbsp;</strong>
                    {data.frequency === DepositFrequency.REPEAT_EVERY ? (
                      <>
                        {' '}
                        {t('additionalTargets_every')} {data.repeatYears}
                        {' '}
                        {pluralize(data.repeatYears!, t('general_repeatYear'), t('general_repeatYears'))}
                      </>
                    ) : (
                      <> {t(`general_frequency_${data.frequency}`)}</>
                    )}
                  </span>
                  {data.inflation?.enabled && (
                    <span>
                      {t('general_inflationPlanned')}: {fractionToPercent(data.inflation.rate)}
                    </span>
                  )}
                  {data.active && (
                    <span>
                      <strong>{t('additionalTargets_active')}</strong>
                    </span>
                  )}
                </>
              ) : (
                <>
                  {data.value !== undefined && data.value.value > 0 ? (
                    <>
                      {data.date && data.elapsed !== undefined && data.elapsed !== null && (
                        <span className={data.elapsed > 30 ? classes.error : ''}>
                          {`${sqlDateToLabel(data.date, 'dd.MM.yyyy')}`}
                          {data.elapsed !== 0 &&
                            ` (${data.elapsed} ${pluralize(
                              data.elapsed,
                              t('general_day'),
                              t('general_days234'),
                              t('general_days'),
                            )}) `}
                        </span>
                      )}
                      <span>
                        <strong>{formatAmountValue(data.value.value, currency)}</strong>&nbsp;
                        {data.until && (
                          <>
                            {t('general_frequency_MONTHLY')}&nbsp;
                            {`${t('general_until')} ${sqlDateToLabel(data.until, 'MM/yyyy')}`}
                          </>
                        )}
                      </span>
                      {data.inflation?.enabled && (
                        <span>
                          {t('general_inflationPlanned')}: {fractionToPercent(data.inflation.rate)}
                        </span>
                      )}
                      {data.until && (
                        <span>
                          <strong>{t('additionalTargets_active')}</strong>
                        </span>
                      )}
                    </>
                  ) : (
                    ' '
                  )}
                </>
              )}
            </Grid>
          </Grid>

          {!userMode && !last && !dashboard && !overview && (
            <>
              <Grid item xs={data.type === DepositType.REGULAR && !mobile ? 5 : 2} className={classes.actions}>
                <Tooltip title={readOnly ? t('accountDetail_readOnlyInfo') : ''}>
                  <div>
                    {data.value ? (
                      <>
                        {data.type === DepositType.REGULAR &&
                          targetsFullfilled !== undefined &&
                          !mobile &&
                          renderCalcButton(data)}

                        <IconButton
                          size="medium"
                          className="accountEdit-button"
                          onClick={() => {
                            editDeposit(data);
                          }}
                          disabled={readOnly}
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="medium"
                          className="account-delete-button"
                          onClick={() => {
                            setConfirmDeleteId(data.id);
                            setConfirmDialogType(data.type);
                            setConfirmDialog(true);
                          }}
                          disabled={readOnly}
                        >
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          editDeposit(data);
                        }}
                        disabled={readOnly}
                      >
                        {t('general_add')}
                      </Button>
                    )}
                  </div>
                </Tooltip>
              </Grid>

              {data.type === DepositType.REGULAR && targetsFullfilled !== undefined && mobile && (
                <>
                  <Grid item xs={2} sm={2} xl={1} className={classes.icon} />
                  <Grid item xs={5}>
                    {renderCalcButton(data)}
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderDepositMoney = () => {
    return (
      <Grid container item xs={12} sm={12} md={12} lg={plan ? 6 : 12} spacing={1} alignItems="stretch">
        <Grid
          container
          item
          className={`${classes.item} ${classes.depositMoney}`}
          alignContent="center"
          alignItems="center"
          onClick={() => handleDepositModal && handleDepositModal(true)}
        >
          <InputIcon />
          {t('accountDetail_depositMoney')}
        </Grid>
      </Grid>
    );
  };

  const renderEmployer = (data: DepositsData) => {
    return (
      <>
        <Grid container spacing={0}>
          <Grid container spacing={0} alignItems="center">
            <Grid item style={{ paddingLeft: '8px' }}>
              {!userMode && (
                <FreeSwitch
                  id={`employerAllowed`}
                  label={`${t('deposits_employerAllowed')}`}
                  checked={data.employerAllowed}
                  afterChange={async () => {
                    await depositsStore.toggleEmployerAllowed(!data?.employerAllowed);
                    handleRefresh && (await handleRefresh());
                    if (data.employerAllowed) setEmployersDialog(true);
                  }}
                  disabled={readOnly}
                />
              )}
            </Grid>
            {viewMode === ViewMode.DETAIL && data.employerAllowed && !readOnly && (
              <>
                <Grid item style={{ paddingLeft: '8px' }}>
                  <Tooltip
                    title={t(!employersOk ? 'globalErrors_account.clientData.employerData.missing' : 'employers')}
                  >
                    <IconButton size="medium" onClick={() => setEmployersDialog(true)} className={classes.iconButton}>
                      <EmployerIcon transparentBgr fontSize="inherit" />
                      {!employersOk && (
                        <ErrorIcon
                          htmlColor={EdwardColors.ALERT}
                          style={{
                            position: 'absolute',
                            background: 'rgba(255,255,255,0.8)',
                            borderRadius: '50%',
                            top: 0,
                            right: '-8px',
                          }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                </Grid>
                <ConfirmDialog
                  isOpen={employersDialog}
                  noHeader
                  close={() => setEmployersDialog(false)}
                  btnCloseText="general_close"
                  maxWidth="md"
                >
                  <Employers
                    accountId={accountId}
                    rootStore={rootStore}
                    readOnly={false}
                    viewMode={ViewMode.DETAIL}
                    refresh={() => setRefreshEmployers(true)}
                    //error={formikProps.errors.employerData}
                  />
                </ConfirmDialog>
              </>
            )}
          </Grid>
          <Grid item xs={12}>
            &nbsp;
          </Grid>
          {data.employerAllowed && (
            <>
              <Grid item>
                <Typography variant="h3" className={classes.moduleTitle}>
                  {t('deposits_employer_title')}
                </Typography>
              </Grid>
              {!readOnly && !userMode && !data.employer && (
                <Grid item style={{ marginLeft: '16px' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setDepositData({ type: DepositType.EMPLOYER });
                      setIsModalOpen(true);
                    }}
                    startIcon={<AddIcon fontSize="medium" />}
                    className="secondaryGreen"
                  >
                    {t('add_deposit')}
                  </Button>
                </Grid>
              )}
            </>
          )}
        </Grid>

        <Grid
          container
          spacing={0}
          alignItems="stretch"
          className={`${classes.moduleWrapper} ${!data.employer ? classes.dummy : ''}`}
        >
          {data.employer && renderDeposit({ ...data.employer, type: DepositType.EMPLOYER })}
        </Grid>
      </>
    );
  };

  return data ? (
    <>
      <DepositModal
        currency={currency}
        opened={isModalOpen}
        handleClose={handleModalClose}
        depositsStore={depositsStore}
        data={depositData}
        options={data.options}
        refresh={handleRefresh}
        defaultInflation={defaultInflation!}
        defaultInflationType={defaultInflationType}
        plan={plan}
        calculateConditions={calculateConditionsMatch}
        calculateInacc={calculateInaccTarget}
      />

      <ConfirmDialog
        isOpen={confirmDialog}
        noHeader
        close={() => setConfirmDialog(false)}
        confirm={async () => {
          if (
            confirmDialogType &&
            [DepositType.SINGLE, DepositType.REGULAR, DepositType.EMPLOYER].includes(confirmDialogType)
          ) {
            await depositsStore?.deleteDeposit(confirmDialogType);
          } else {
            await depositsStore?.deleteFutureDeposit(confirmDeleteId!);
          }
          setConfirmDialog(false);
          handleRefresh();
        }}
      >
        {t(`deposits_confirmDelete_${confirmDialogType}`)}
      </ConfirmDialog>

      {!overview && (
        <Typography variant="h3" className={classes.moduleTitle}>
          {t('deposits_plannedDeposits')}
        </Typography>
      )}

      {viewMode !== ViewMode.WITHDRAW && (
        <Grid container spacing={0} alignItems="stretch" className={classes.moduleWrapper}>
          <>
            {(!readOnly || data?.initial) && renderDeposit({ type: DepositType.SINGLE, value: data.initial?.value })}
            {data.periodic &&
              renderDeposit({
                type: DepositType.REGULAR,
                value: data.periodic.value,
                until: data?.periodic.until,
                inflation: data.periodic.inflation,
              })}
            {dashboard &&
              data.lastPayment &&
              renderDeposit({
                type: DepositType.LAST,
                value: data.lastPayment.value,
                elapsed: data.lastPayment.elapsed,
                date: data.lastPayment.date,
              })}
            {dashboard && userMode && !locked && renderDepositMoney()}
          </>
        </Grid>
      )}

      {viewMode !== ViewMode.WITHDRAW && (data.deposits?.length > 0 || (!readOnly && !userMode)) && (
        <Grid container spacing={0}>
          <Grid item>
            <Typography variant="h3" className={classes.moduleTitle}>
              {t('deposits_futureDeposits')}
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: '16px' }}>
            {!readOnly && !userMode && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setDepositData({ type: DepositType.FUTURE });
                  setIsModalOpen(true);
                }}
                startIcon={<AddIcon fontSize="medium" />}
                className="secondaryGreen"
              >
                {t('add_deposit')}
              </Button>
            )}
          </Grid>
        </Grid>
      )}
      <Grid
        container
        spacing={0}
        alignItems="stretch"
        className={`${classes.moduleWrapper} ${
          !data.deposits?.length ? (overview ? classes.empty : classes.dummy) : ''
        }`}
      >
        {data.deposits.map((deposit) => renderDeposit(deposit))}
      </Grid>

      {mode === AccountMode.LTIP && viewMode !== ViewMode.WITHDRAW && renderEmployer(data)}
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Deposits);
